import { ImagePlus, RotateCcw, Trash2 } from '@/components/ui/icons';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  AVATAR_DECORATIONS,
  BLAZE_NAME,
  isBlazeStyle,
  isHalloweenStyle,
  LIMITS,
  NAME_EFFECTS,
  NAME_FONTS,
  PROFILE_EFFECTS,
  updateProfileSchema,
  type AvatarDecorationId,
  type NameEffectId,
  type NameFontId,
  type ProfileEffectId,
  type PublicUser,
} from '@clanfire/shared';
import { userApi } from '@/api/endpoints';
import { AvatarDecoration } from '@/components/profile/AvatarDecoration';
import { ProfileBio, ProfileCard } from '@/components/profile/ProfileCard';
import { ProfileEffect } from '@/components/profile/ProfileEffect';
import { StyledName } from '@/components/profile/StyledName';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input, TextArea } from '@/components/ui/Input';
import { useSubmit, validate } from '@/hooks/useForm';
import { CLEAR_AFTER_OPTIONS, clearAfterToIso, describeExpiry, type ClearAfter } from '@/lib/customStatus';
import { DEFAULT_GRADIENT_END, NAME_FONT_FAMILY } from '@/lib/profileStyle';
import { cn, initials } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { useHasBlaze } from '@/stores/blaze';
import { toast, useUi } from '@/stores/ui';
import { usePresenceOf } from '@/stores/users';
import { ImagePicker } from '../modals/ImagePicker';
import { BlazeTag } from './BlazeSection';
import { SettingsCard } from './SettingsShell';

const ACCENTS = ['#ff7a45', '#f59e0b', '#10b981', '#06b6d4', '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#64748b'];
const STATUS_EMOJI = ['', '🚀', '🎮', '🎧', '📚', '☕', '💻', '🌙', '🏖️', '🤒', '🎉'];
const THEME_PRESETS: Array<[string, string, string]> = [
  ['Ember', '#7c2d12', '#1c1917'],
  ['Sunset', '#f97316', '#be185d'],
  ['Ocean', '#0e7490', '#1e3a8a'],
  ['Forest', '#166534', '#14532d'],
  ['Grape', '#6d28d9', '#312e81'],
  ['Candy', '#f9a8d4', '#c4b5fd'],
  ['Mint', '#a7f3d0', '#67e8f9'],
  ['Midnight', '#0f172a', '#312e81'],
  ['Gold', '#fbbf24', '#b45309'],
];
const MAX_IMAGE = 8 * 1024 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/** Object URL for a chosen file, cleaned up automatically. */
export function useObjectUrl(blob: Blob | null | undefined): string | null | undefined {
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : blob), [blob]);
  useEffect(() => () => void (typeof url === 'string' && URL.revokeObjectURL(url)), [url]);
  return url;
}

/**
 * The profile editor: everything (banner, animated avatar, name styles, theme,
 * decoration, effect…) is free, apart from the extras marked Blaze, which anyone
 * can try in the preview. The right column is a live preview.
 */
export function ProfileEditor() {
  const user = useAuth((s) => s.user)!;
  const presence = usePresenceOf(user.id);
  const initial = useMemo(
    () => ({
      displayName: user.displayName,
      bio: user.bio,
      accentColor: user.accentColor,
      customStatus: user.customStatus ?? '',
      customStatusEmoji: user.customStatusEmoji ?? '',
      themePrimary: user.themePrimary,
      themeAccent: user.themeAccent,
      avatarDecoration: user.avatarDecoration,
      profileEffect: user.profileEffect,
      nameFont: user.nameFont,
      nameEffect: user.nameEffect,
      nameColor: user.nameColor,
      nameColor2: user.nameColor2,
    }),
    // Re-baseline only after a save (the user object changes).
    [user],
  );
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));

  const [avatar, setAvatar] = useState<Blob | null | undefined>(undefined);
  const [banner, setBanner] = useState<Blob | null | undefined>(undefined);
  const avatarPreview = useObjectUrl(avatar);
  const bannerPreview = useObjectUrl(banner);
  const initialClearAfter: ClearAfter = user.customStatusExpiresAt ? 'keep' : 'never';
  const [clearAfter, setClearAfter] = useState<ClearAfter>(initialClearAfter);
  const expiryLabel = describeExpiry(user.customStatusExpiresAt);
  const { loading, error, fields, setFields, run } = useSubmit();

  const hasBlaze = useHasBlaze();
  const needsBlaze =
    !hasBlaze &&
    (isBlazeStyle(NAME_EFFECTS, form.nameEffect) || isBlazeStyle(AVATAR_DECORATIONS, form.avatarDecoration) || isBlazeStyle(PROFILE_EFFECTS, form.profileEffect));

  const dirty =
    avatar !== undefined || banner !== undefined || clearAfter !== initialClearAfter || (Object.keys(form) as Array<keyof typeof form>).some((k) => form[k] !== initial[k]);

  // What the preview shows: the saved profile with every pending change applied.
  const draft: PublicUser = {
    ...user,
    ...form,
    customStatus: form.customStatus.trim() || null,
    customStatusEmoji: form.customStatus.trim() ? form.customStatusEmoji || null : null,
    avatarUrl: avatar === undefined ? user.avatarUrl : (avatarPreview ?? null),
    bannerUrl: banner === undefined ? user.bannerUrl : (bannerPreview ?? null),
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      displayName: form.displayName,
      bio: form.bio,
      accentColor: form.accentColor,
      customStatus: form.customStatus.trim() || null,
      customStatusEmoji: form.customStatus.trim() || form.customStatusEmoji ? form.customStatusEmoji || null : null,
      customStatusExpiresAt: form.customStatus.trim() ? clearAfterToIso(clearAfter, user.customStatusExpiresAt) : null,
      themePrimary: form.themePrimary,
      themeAccent: form.themeAccent,
      avatarDecoration: form.avatarDecoration,
      profileEffect: form.profileEffect,
      nameFont: form.nameFont,
      nameEffect: form.nameEffect,
      nameColor: form.nameColor,
      nameColor2: form.nameColor2,
    };
    const { data, fields: invalid } = validate(updateProfileSchema, payload);
    if (!data) return setFields(invalid);
    const updated = await run(async () => {
      let result = await userApi.updateProfile(data);
      if (avatar !== undefined) result = avatar ? await userApi.uploadAvatar(avatar) : await userApi.removeAvatar();
      if (banner !== undefined) result = banner ? await userApi.uploadBanner(banner) : await userApi.removeBanner();
      return result;
    });
    if (updated) {
      useAuth.getState().setUser(updated);
      setAvatar(undefined);
      setBanner(undefined);
      toast.success('Profile saved.');
    }
  };

  const reset = () => {
    setForm(initial);
    setAvatar(undefined);
    setBanner(undefined);
    setClearAfter(initialClearAfter);
    setFields({});
  };

  return (
    <form onSubmit={save} className="@container/editor grid gap-6 @4xl/editor:grid-cols-[minmax(0,1fr)_320px]">
      <div className="@container flex min-w-0 flex-col">
        <SettingsCard title="Avatar" description="PNG, JPEG, WebP or GIF. Animated GIFs stay animated.">
          <ImagePicker
            key={user.avatarUrl ?? 'none'}
            currentUrl={user.avatarUrl}
            fallback={<span className="text-2xl font-bold text-fg-muted">{initials(form.displayName)}</span>}
            onChange={setAvatar}
          />
        </SettingsCard>

        <SettingsCard title="Banner" description="A wide image across the top of your profile (animated GIFs work too). Without one, your banner colour is used.">
          <BannerPicker url={draft.bannerUrl} color={form.accentColor} onChange={setBanner} />
          <p className="mt-4 mb-1.5 text-xs font-bold tracking-wide text-fg-soft uppercase">Banner colour</p>
          <ColorSwatches value={form.accentColor} options={ACCENTS} onChange={(c) => set('accentColor', c)} label="banner colour" />
        </SettingsCard>

        <SettingsCard title="Name">
          <Input label="Display name" value={form.displayName} onChange={(e) => set('displayName', e.target.value)} error={fields.displayName} maxLength={LIMITS.displayNameMax} />
        </SettingsCard>

        <SettingsCard title="Display name style" description="Shown on your profile, in chat and in member lists.">
          <p className="mb-2 text-xs font-bold tracking-wide text-fg-soft uppercase">Font</p>
          <div className="grid grid-cols-4 gap-2 @xl:grid-cols-7">
            <OptionTile selected={!form.nameFont} onClick={() => set('nameFont', null)} label="Default">
              <span className="text-lg font-bold">Aa</span>
            </OptionTile>
            {NAME_FONTS.map((f) => (
              <OptionTile key={f.id} selected={form.nameFont === f.id} onClick={() => set('nameFont', f.id as NameFontId)} label={f.label}>
                <span className="text-lg" style={NAME_FONT_FAMILY[f.id]}>
                  Aa
                </span>
              </OptionTile>
            ))}
          </div>
          <p className="mt-4 mb-2 text-xs font-bold tracking-wide text-fg-soft uppercase">Effect</p>
          <div className="grid grid-cols-3 gap-2 @xl:grid-cols-5">
            <OptionTile selected={!form.nameEffect} onClick={() => set('nameEffect', null)} label="None">
              <span className="font-bold">{form.displayName.slice(0, 8) || 'Name'}</span>
            </OptionTile>
            {NAME_EFFECTS.map((fx) => (
              <OptionTile key={fx.id} selected={form.nameEffect === fx.id} onClick={() => set('nameEffect', fx.id as NameEffectId)} label={fx.label} blaze={isBlazeStyle(NAME_EFFECTS, fx.id)} halloween={isHalloweenStyle(NAME_EFFECTS, fx.id)}>
                <StyledName user={{ ...draft, nameEffect: fx.id as NameEffectId }} className="font-bold">
                  {form.displayName.slice(0, 8) || 'Name'}
                </StyledName>
              </OptionTile>
            ))}
          </div>
          {(form.nameEffect === 'solid' || form.nameEffect === 'gradient' || form.nameEffect === 'neon') && (
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <ColorField label={form.nameEffect === 'gradient' ? 'Start' : 'Colour'} value={form.nameColor ?? form.accentColor} onChange={(c) => set('nameColor', c)} />
              {form.nameEffect === 'gradient' && <ColorField label="End" value={form.nameColor2 ?? DEFAULT_GRADIENT_END} onChange={(c) => set('nameColor2', c)} />}
            </div>
          )}
        </SettingsCard>

        <SettingsCard title="About me" description="Supports **bold**, *italics*, links, :emoji: and more.">
          <TextArea value={form.bio} onChange={(e) => set('bio', e.target.value)} error={fields.bio} maxLength={LIMITS.bioMax} hint={`${form.bio.length}/${LIMITS.bioMax}`} aria-label="About me" />
        </SettingsCard>

        <SettingsCard title="Custom status">
          <div className="flex gap-2">
            <select
              value={form.customStatusEmoji}
              onChange={(e) => set('customStatusEmoji', e.target.value)}
              aria-label="Status emoji"
              className="h-11 w-16 rounded-xl border border-line bg-sunken text-center text-lg"
            >
              {STATUS_EMOJI.map((e) => (
                <option key={e || 'none'} value={e}>
                  {e || '—'}
                </option>
              ))}
            </select>
            <div className="flex-1">
              <Input value={form.customStatus} onChange={(e) => set('customStatus', e.target.value)} placeholder="What's happening?" maxLength={LIMITS.customStatusMax} aria-label="Custom status" error={fields.customStatus} />
            </div>
          </div>
          {form.customStatus.trim() && (
            <label className="mt-2 flex items-center gap-2 text-sm text-fg-soft">
              Clear after
              <select value={clearAfter} onChange={(e) => setClearAfter(e.target.value as ClearAfter)} className="h-9 rounded-lg border border-line bg-sunken px-2 text-sm text-fg">
                {expiryLabel && <option value="keep">Keep current ({expiryLabel.replace('Clears ', '').replace(/^at /, '')})</option>}
                {CLEAR_AFTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </SettingsCard>

        <SettingsCard title="Profile theme" description="Two colours that tint your whole profile card.">
          <div className="flex flex-wrap gap-2">
            {THEME_PRESETS.map(([name, top, bottom]) => (
              <button
                key={name}
                type="button"
                title={name}
                aria-label={`${name} theme`}
                aria-pressed={form.themePrimary === top && form.themeAccent === bottom}
                onClick={() => setForm((f) => ({ ...f, themePrimary: top, themeAccent: bottom }))}
                className={cn(
                  'h-10 w-10 rounded-xl ring-offset-2 ring-offset-sidebar transition-transform hover:scale-110',
                  form.themePrimary === top && form.themeAccent === bottom && 'ring-2 ring-fg',
                )}
                style={{ background: `linear-gradient(180deg, ${top}, ${bottom})` }}
              />
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <ColorField label="Top" value={form.themePrimary ?? '#2a2433'} onChange={(c) => set('themePrimary', c)} />
            <ColorField label="Bottom" value={form.themeAccent ?? form.themePrimary ?? '#1b1822'} onChange={(c) => set('themeAccent', c)} />
            {(form.themePrimary || form.themeAccent) && (
              <Button variant="ghost" size="sm" icon={<RotateCcw size={14} />} onClick={() => setForm((f) => ({ ...f, themePrimary: null, themeAccent: null }))}>
                No theme
              </Button>
            )}
          </div>
        </SettingsCard>

        <SettingsCard title="Avatar decoration">
          <div className="grid grid-cols-3 gap-2 @md:grid-cols-4 @xl:grid-cols-5">
            <OptionTile selected={!form.avatarDecoration} onClick={() => set('avatarDecoration', null)} label="None" tall>
              <Avatar user={{ ...draft, avatarDecoration: null }} size={44} />
            </OptionTile>
            {AVATAR_DECORATIONS.map((d) => (
              <OptionTile key={d.id} selected={form.avatarDecoration === d.id} onClick={() => set('avatarDecoration', d.id as AvatarDecorationId)} label={d.label} tall blaze={isBlazeStyle(AVATAR_DECORATIONS, d.id)} halloween={isHalloweenStyle(AVATAR_DECORATIONS, d.id)}>
                <span className="relative inline-flex">
                  <Avatar user={{ ...draft, avatarDecoration: null }} size={44} />
                  <AvatarDecoration id={d.id as AvatarDecorationId} size={44} />
                </span>
              </OptionTile>
            ))}
          </div>
        </SettingsCard>

        <SettingsCard title="Profile effect" description="An animated layer over your profile card. People who turn on Reduce motion won't see it move.">
          <div className="grid grid-cols-2 gap-2 @md:grid-cols-3">
            <OptionTile selected={!form.profileEffect} onClick={() => set('profileEffect', null)} label="None" tall>
              <span className="text-sm text-fg-muted">No effect</span>
            </OptionTile>
            {PROFILE_EFFECTS.map((fx) => (
              <OptionTile key={fx.id} selected={form.profileEffect === fx.id} onClick={() => set('profileEffect', fx.id as ProfileEffectId)} label={fx.label} tall blaze={isBlazeStyle(PROFILE_EFFECTS, fx.id)} halloween={isHalloweenStyle(PROFILE_EFFECTS, fx.id)}>
                <span className="absolute inset-0 overflow-hidden rounded-[inherit] bg-gradient-to-b from-[#2a2238] to-[#14111a]">
                  <ProfileEffect id={fx.id as ProfileEffectId} />
                </span>
              </OptionTile>
            ))}
          </div>
        </SettingsCard>

        {error && <p className="mb-3 text-sm text-danger">{error}</p>}
        {needsBlaze && (
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-[#f97316]/40 bg-[#f97316]/10 p-3 text-sm">
            <span className="flex-1">The styles marked Blaze come with {BLAZE_NAME}. You can try them here, but you need Blaze to save them.</span>
            <Button size="sm" onClick={() => useUi.getState().openModal({ type: 'userSettings', section: 'blaze' })}>
              See Blaze
            </Button>
          </div>
        )}
        <div className="sticky bottom-0 z-10 -mx-1 flex items-center justify-end gap-2 rounded-xl border border-line bg-elevated/95 px-3 py-2 shadow-pop backdrop-blur">
          <span className="mr-auto text-sm text-fg-muted">{dirty ? 'You have unsaved changes.' : 'All changes saved.'}</span>
          <Button variant="ghost" disabled={!dirty || loading} onClick={reset}>
            Reset
          </Button>
          <Button type="submit" loading={loading} disabled={!dirty || needsBlaze}>
            Save changes
          </Button>
        </div>
      </div>

      <aside aria-label="Preview" className="@4xl/editor:sticky @4xl/editor:top-0 @4xl/editor:self-start">
        <p className="mb-1.5 text-xs font-bold tracking-wide text-fg-soft uppercase">Preview</p>
        <div className="overflow-hidden rounded-2xl border border-line">
          <ProfileCard user={draft} presence={presence}>
            <div className="mt-4 space-y-4 rounded-xl bg-sunken p-4 text-sm">
              <ProfileBio bio={draft.bio} />
              <section>
                <h3 className="mb-1 text-[11px] font-bold tracking-wider text-fg-muted uppercase">Member since</h3>
                <p className="text-fg-soft">{new Date(user.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </section>
            </div>
          </ProfileCard>
        </div>
        <p className="mt-4 mb-1.5 text-xs font-bold tracking-wide text-fg-soft uppercase">In chat</p>
        <div className="flex gap-3 rounded-2xl border border-line bg-surface p-3">
          <Avatar user={draft} size={40} />
          <div className="min-w-0">
            <p className="flex items-baseline gap-2">
              <StyledName user={draft} className="truncate font-semibold">
                {form.displayName || user.username}
              </StyledName>
              <span className="text-xs text-fg-muted">Today at 12:00</span>
            </p>
            <p className="text-[15px] text-fg-soft">This is how your messages look.</p>
          </div>
        </div>
      </aside>
    </form>
  );
}

function OptionTile({
  selected,
  onClick,
  label,
  tall,
  blaze,
  halloween,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  tall?: boolean;
  /** Comes with Blaze (marked in the corner). */
  blaze?: boolean;
  /** A Halloween style, marked in the corner during October. */
  halloween?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      aria-label={blaze ? `${label} (Blaze)` : undefined}
      className={cn(
        'group relative flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 bg-sunken p-2 text-center transition-colors',
        selected ? 'border-accent' : 'border-transparent hover:border-line-strong',
      )}
    >
      <span className={cn('relative flex w-full items-center justify-center overflow-visible rounded-lg', tall ? 'h-16' : 'h-10')}>{children}</span>
      <span className={cn('w-full truncate text-xs', selected ? 'font-semibold text-fg' : 'text-fg-muted')}>{label}</span>
      {blaze && <BlazeTag className="absolute top-1 right-1 z-10" />}
      {halloween && new Date().getMonth() === 9 && (
        <span className="absolute top-1 left-1 z-10 rounded-full bg-[#3b1f52] px-1.5 py-0.5 text-[9px] leading-none font-extrabold tracking-wide text-[#fdba74]">🎃 HALLOWEEN</span>
      )}
    </button>
  );
}

function ColorSwatches({ value, options, onChange, label }: { value: string; options: string[]; onChange: (color: string) => void; label: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {options.map((c) => (
        <button
          key={c}
          type="button"
          aria-label={`Use ${label} ${c}`}
          aria-pressed={value === c}
          onClick={() => onChange(c)}
          className={cn('h-8 w-8 rounded-full ring-offset-2 ring-offset-sidebar transition-transform hover:scale-110', value === c && 'ring-2 ring-fg')}
          style={{ background: c }}
        />
      ))}
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} aria-label={`Custom ${label}`} className="h-8 w-10 cursor-pointer rounded border-0 bg-transparent" />
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (color: string) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-fg-soft">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-11 cursor-pointer rounded-lg border border-line bg-transparent" />
      {label}
    </label>
  );
}

export function BannerPicker({ url, color, onChange }: { url: string | null; color: string; onChange: (file: Blob | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-3">
      <div className="relative h-24 overflow-hidden rounded-xl border-2 border-dashed border-line-strong" style={{ background: url ? undefined : `linear-gradient(135deg, ${color}, ${color}55)` }}>
        {url && <img src={url} alt="" className="h-full w-full object-cover" />}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" icon={<ImagePlus size={15} />} onClick={() => input.current?.click()}>
          Upload banner
        </Button>
        {url && (
          <Button variant="ghost" size="sm" icon={<Trash2 size={15} />} onClick={() => onChange(null)}>
            Remove
          </Button>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept={IMAGE_TYPES.join(',')}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          if (!IMAGE_TYPES.includes(file.type)) return toast.error('Please choose a PNG, JPEG, GIF or WebP image.');
          if (file.size > MAX_IMAGE) return toast.error('Banners must be under 8 MB.');
          onChange(file);
        }}
      />
    </div>
  );
}
